/**
 * AttendanceTab Component (Revamped with React Big Calendar)
 * CEO-friendly view with Interactive Calendar, Individual Reports, and Team Overview
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, Download, Users, ChevronLeft, ChevronRight, User, BarChart3, TrendingUp, Filter, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { attendanceAPI } from '@/lib/api';

// React Big Calendar imports
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Setup the localizer for react-big-calendar
const localizer = momentLocalizer(moment);

export const AttendanceTab = ({ 
  employees,
  todayAttendance,
  attendanceData,
  attendanceViewMode,
  setAttendanceViewMode,
  attendanceDateRange,
  setAttendanceDateRange,
  attendanceEmployeeFilter,
  setAttendanceEmployeeFilter,
  onFetchTodayAttendance,
  onFetchAttendanceData,
  user
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeReport, setEmployeeReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [selectedDateRecords, setSelectedDateRecords] = useState(null);

  // Load calendar data - called manually when needed, not in useEffect to avoid loops
  const loadCalendarData = useCallback(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth() + 1;
    const start = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
    setAttendanceDateRange({ start, end });
    setTimeout(() => onFetchAttendanceData?.(), 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarDate]);

  // Transform attendance data to calendar events
  const calendarEvents = useMemo(() => {
    if (!attendanceData?.records) return [];
    
    const records = attendanceEmployeeFilter && attendanceEmployeeFilter !== 'all'
      ? attendanceData.records.filter(r => r.user_id === attendanceEmployeeFilter)
      : attendanceData.records;
    
    // Group records by date
    const groupedByDate = records.reduce((acc, record) => {
      const date = record.date;
      if (!acc[date]) acc[date] = [];
      acc[date].push(record);
      return acc;
    }, {});

    // Create events for each day
    return Object.entries(groupedByDate).map(([date, dayRecords]) => {
      const presentCount = dayRecords.filter(r => (r.status || r.location_status) === 'present').length;
      const remoteCount = dayRecords.filter(r => (r.status || r.location_status) === 'remote').length;
      const totalHours = dayRecords.reduce((sum, r) => sum + (r.total_hours || 0), 0);
      
      return {
        id: date,
        title: attendanceEmployeeFilter === 'all' 
          ? `${presentCount + remoteCount} checked in`
          : dayRecords[0]?.user_name || 'Attendance',
        start: new Date(date),
        end: new Date(date),
        allDay: true,
        resource: {
          presentCount,
          remoteCount,
          totalHours: totalHours.toFixed(1),
          records: dayRecords,
          status: attendanceEmployeeFilter !== 'all' ? (dayRecords[0]?.status || dayRecords[0]?.location_status) : 'mixed'
        }
      };
    });
  }, [attendanceData, attendanceEmployeeFilter]);

  // Custom event component for the calendar
  const EventComponent = ({ event }) => {
    const { resource } = event;
    const isIndividual = attendanceEmployeeFilter && attendanceEmployeeFilter !== 'all';
    
    if (isIndividual) {
      return (
        <div className={`px-1 py-0.5 rounded text-xs font-medium ${
          resource.status === 'present' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
        }`}>
          {resource.status === 'present' ? 'Office' : 'Remote'}
          {resource.totalHours > 0 && ` (${resource.totalHours}h)`}
        </div>
      );
    }
    
    return (
      <div className="text-xs">
        <div className="flex flex-wrap gap-1">
          {resource.presentCount > 0 && (
            <span className="bg-green-500 text-white px-1 rounded">{resource.presentCount} office</span>
          )}
          {resource.remoteCount > 0 && (
            <span className="bg-blue-500 text-white px-1 rounded">{resource.remoteCount} remote</span>
          )}
        </div>
      </div>
    );
  };

  // Custom day styling
  const dayPropGetter = (date) => {
    const isToday = moment(date).isSame(moment(), 'day');
    const isWeekend = date.getDay() === 0; // Sunday
    
    return {
      className: `${isToday ? 'rbc-today-highlight' : ''} ${isWeekend ? 'rbc-weekend' : ''}`,
      style: {
        backgroundColor: isToday ? 'rgba(20, 184, 166, 0.1)' : isWeekend ? 'rgba(239, 68, 68, 0.05)' : undefined,
      }
    };
  };

  // Handle event selection (clicking on a day with events)
  const handleSelectEvent = (event) => {
    setSelectedDateRecords({
      date: event.start,
      records: event.resource.records
    });
  };

  // Handle slot selection (clicking on an empty day)
  const handleSelectSlot = ({ start }) => {
    const dateStr = moment(start).format('YYYY-MM-DD');
    const records = attendanceData?.records?.filter(r => r.date === dateStr) || [];
    if (records.length > 0) {
      setSelectedDateRecords({ date: start, records });
    }
  };

  // Navigate calendar
  const handleNavigate = (newDate) => {
    setCalendarDate(newDate);
    // Load data for the new month
    const year = newDate.getFullYear();
    const month = newDate.getMonth() + 1;
    const start = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
    setAttendanceDateRange({ start, end });
    setTimeout(() => onFetchAttendanceData?.(), 100);
  };

  // View individual employee report
  const viewEmployeeReport = async (emp) => {
    setSelectedEmployee(emp);
    setLoadingReport(true);
    try {
      const year = calendarDate.getFullYear();
      const month = calendarDate.getMonth() + 1;
      const start = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
      
      const res = await attendanceAPI.adminGetAll({
        user_id: emp.user_id || emp.id,
        start_date: start,
        end_date: end
      });
      
      // Calculate stats
      const records = res.data?.records || [];
      const presentDays = records.filter(r => (r.status || r.location_status) === 'present').length;
      const remoteDays = records.filter(r => (r.status || r.location_status) === 'remote').length;
      const totalHours = records.reduce((sum, r) => sum + (r.total_hours || 0), 0);
      const lateDays = records.filter(r => r.time_status === 'late').length;
      const onTimeDays = records.filter(r => r.time_status === 'on_time').length;
      
      // Calculate working days (excluding Sundays)
      const startDate = new Date(start);
      const endDate = new Date() < new Date(end) ? new Date() : new Date(end);
      let workingDays = 0;
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        if (d.getDay() !== 0) workingDays++; // Count all days except Sunday
      }
      
      const attendanceRate = workingDays > 0 ? Math.round((records.length / workingDays) * 100) : 0;
      
      setEmployeeReport({
        employee: emp,
        records,
        stats: {
          totalDays: records.length,
          presentDays,
          remoteDays,
          absentDays: Math.max(0, workingDays - records.length),
          totalHours: totalHours.toFixed(1),
          avgHours: records.length > 0 ? (totalHours / records.length).toFixed(1) : 0,
          lateDays,
          onTimeDays,
          attendanceRate,
          workingDays
        }
      });
    } catch (error) {
      console.error('Error fetching employee report:', error);
      toast.error('Failed to load employee report');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const csvContent = [
        ['Employee', 'Email', 'Date', 'Status', 'Check In', 'Check Out', 'Hours', 'Location', 'Time Status'].join(','),
        ...(attendanceData?.records || []).map(r => [
          `"${r.user_name || ''}"`,
          r.user_email || r.email || '',
          r.date,
          r.status || r.location_status || 'present',
          r.check_in_time_pkt || '-',
          r.check_out_time_pkt || '-',
          r.total_hours?.toFixed(1) || '-',
          r.check_in_location?.office_name || (r.status === 'present' ? 'Office' : 'Remote'),
          r.time_status || '-'
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const empName = attendanceEmployeeFilter !== 'all' 
        ? employees.find(e => (e.user_id || e.id) === attendanceEmployeeFilter)?.full_name?.replace(/\s/g, '_') 
        : 'all';
      a.download = `attendance_${empName}_${attendanceDateRange.start}_to_${attendanceDateRange.end}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully!');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  // Print report
  const handlePrint = () => {
    window.print();
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    return timeStr.includes('AM') || timeStr.includes('PM') ? timeStr : new Date(timeStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  // Summary stats for today
  const todayStats = useMemo(() => {
    const attendance = todayAttendance?.attendance || [];
    return {
      total: employees.length,
      present: attendance.filter(a => a.status === 'present').length,
      remote: attendance.filter(a => a.status === 'remote').length,
      absent: attendance.filter(a => a.status === 'absent' || (!a.checked_in && !a.status)).length
    };
  }, [todayAttendance, employees]);

  // Monthly stats
  const monthlyStats = useMemo(() => {
    const records = attendanceData?.records || [];
    const uniqueEmployees = new Set(records.map(r => r.user_id)).size;
    const totalHours = records.reduce((sum, r) => sum + (r.total_hours || 0), 0);
    const avgHoursPerDay = records.length > 0 ? (totalHours / records.length).toFixed(1) : 0;
    
    return {
      totalRecords: records.length,
      uniqueEmployees,
      totalHours: totalHours.toFixed(0),
      avgHoursPerDay
    };
  }, [attendanceData]);

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-4">
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white shadow-lg" data-testid="attendance-stat-total">
          <p className="text-sm opacity-80">Total Team</p>
          <p className="text-3xl font-bold">{todayStats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg" data-testid="attendance-stat-present">
          <p className="text-sm opacity-80">Present (Office)</p>
          <p className="text-3xl font-bold">{todayStats.present}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg" data-testid="attendance-stat-remote">
          <p className="text-sm opacity-80">Remote</p>
          <p className="text-3xl font-bold">{todayStats.remote}</p>
        </div>
        <div className="bg-gradient-to-br from-red-400 to-red-500 rounded-xl p-4 text-white shadow-lg" data-testid="attendance-stat-absent">
          <p className="text-sm opacity-80">Absent</p>
          <p className="text-3xl font-bold">{todayStats.absent}</p>
        </div>
      </div>

      {/* View Toggle & Filters */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 print:hidden">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
            <Button
              size="sm"
              variant={attendanceViewMode === 'daily' ? 'default' : 'ghost'}
              onClick={() => { setAttendanceViewMode('daily'); onFetchTodayAttendance?.(attendanceEmployeeFilter); }}
              className={attendanceViewMode === 'daily' ? 'bg-teal-600 text-white' : ''}
              data-testid="attendance-view-today"
            >
              <Clock className="w-4 h-4 mr-1" />
              Today
            </Button>
            <Button
              size="sm"
              variant={attendanceViewMode === 'calendar' ? 'default' : 'ghost'}
              onClick={() => { setAttendanceViewMode('calendar'); loadCalendarData(); }}
              className={attendanceViewMode === 'calendar' ? 'bg-teal-600 text-white' : ''}
              data-testid="attendance-view-calendar"
            >
              <CalendarIcon className="w-4 h-4 mr-1" />
              Calendar
            </Button>
            <Button
              size="sm"
              variant={attendanceViewMode === 'team' ? 'default' : 'ghost'}
              onClick={() => { setAttendanceViewMode('team'); loadCalendarData(); }}
              className={attendanceViewMode === 'team' ? 'bg-teal-600 text-white' : ''}
              data-testid="attendance-view-team"
            >
              <Users className="w-4 h-4 mr-1" />
              Team Reports
            </Button>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={attendanceEmployeeFilter} onValueChange={(val) => { 
              setAttendanceEmployeeFilter(val); 
              if (attendanceViewMode === 'daily') {
                onFetchTodayAttendance?.(val);
              } else {
                loadCalendarData();
              }
            }}>
              <SelectTrigger className="w-48" data-testid="attendance-employee-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.user_id || emp.id} value={emp.user_id || emp.id}>
                    {emp.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={isExporting} data-testid="attendance-export-btn">
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="attendance-print-btn">
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Today View */}
      {attendanceViewMode === 'daily' && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
            <h3 className="font-semibold text-gray-800 dark:text-white">
              Today&apos;s Attendance - {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="attendance-today-table">
              <thead className="bg-gray-50 dark:bg-slate-700">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-300 font-medium">Employee</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-300 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-300 font-medium">Check In</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-300 font-medium">Check Out</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-300 font-medium">Location</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-300 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {(todayAttendance?.attendance || []).map((record) => (
                  <tr key={record.user_id} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                          <span className="text-teal-600 font-semibold">{record.user_name?.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 dark:text-white">{record.user_name}</p>
                          <p className="text-xs text-gray-500">{record.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={`${
                        record.status === 'present' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        record.status === 'remote' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {record.status || (record.checked_in ? 'present' : 'absent')}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {formatTime(record.check_in_time_pkt || record.check_in_time)}
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {formatTime(record.check_out_time_pkt || record.check_out_time)}
                    </td>
                    <td className="py-3 px-4">
                      {record.location_verified || record.check_in_location?.verified ? (
                        <Badge className="bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
                          {record.check_in_location?.office_name || 'Office'}
                        </Badge>
                      ) : record.checked_in || record.check_in_time ? (
                        <Badge className="bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800">Remote</Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <Button size="sm" variant="ghost" onClick={() => viewEmployeeReport(record)} className="text-teal-600 hover:text-teal-700 hover:bg-teal-50">
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Report
                      </Button>
                    </td>
                  </tr>
                ))}
                {(!todayAttendance?.attendance || todayAttendance.attendance.length === 0) && (
                  <tr>
                    <td colSpan="6" className="py-12 text-center text-gray-500">
                      <CalendarIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      No attendance data for today
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Interactive Calendar View */}
      {attendanceViewMode === 'calendar' && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 md:p-6">
          {/* Month Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
            <div className="text-center">
              <p className="text-2xl font-bold text-teal-600">{monthlyStats.totalRecords}</p>
              <p className="text-xs text-gray-500">Total Check-ins</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{monthlyStats.uniqueEmployees}</p>
              <p className="text-xs text-gray-500">Active Employees</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{monthlyStats.totalHours}h</p>
              <p className="text-xs text-gray-500">Total Hours</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{monthlyStats.avgHoursPerDay}h</p>
              <p className="text-xs text-gray-500">Avg Hours/Day</p>
            </div>
          </div>

          {/* React Big Calendar */}
          <div className="attendance-calendar" data-testid="attendance-calendar">
            <style>{`
              .attendance-calendar .rbc-calendar {
                min-height: 600px;
              }
              .attendance-calendar .rbc-header {
                padding: 12px 4px;
                font-weight: 600;
                color: #374151;
                background: #f9fafb;
                border-bottom: 2px solid #e5e7eb;
              }
              .dark .attendance-calendar .rbc-header {
                color: #d1d5db;
                background: #334155;
                border-bottom-color: #475569;
              }
              .attendance-calendar .rbc-date-cell {
                padding: 4px 8px;
                text-align: right;
              }
              .attendance-calendar .rbc-date-cell > a {
                color: #374151;
                font-weight: 500;
              }
              .dark .attendance-calendar .rbc-date-cell > a {
                color: #d1d5db;
              }
              .attendance-calendar .rbc-today {
                background-color: rgba(20, 184, 166, 0.1) !important;
              }
              .attendance-calendar .rbc-off-range-bg {
                background-color: #f3f4f6;
              }
              .dark .attendance-calendar .rbc-off-range-bg {
                background-color: #1e293b;
              }
              .attendance-calendar .rbc-event {
                background: transparent;
                border: none;
                padding: 2px;
              }
              .attendance-calendar .rbc-row-segment {
                padding: 0 2px;
              }
              .attendance-calendar .rbc-toolbar {
                margin-bottom: 16px;
                flex-wrap: wrap;
                gap: 8px;
              }
              .attendance-calendar .rbc-toolbar button {
                color: #374151;
                border-color: #d1d5db;
                padding: 6px 12px;
                border-radius: 6px;
              }
              .dark .attendance-calendar .rbc-toolbar button {
                color: #d1d5db;
                border-color: #475569;
              }
              .attendance-calendar .rbc-toolbar button:hover {
                background-color: #f3f4f6;
              }
              .dark .attendance-calendar .rbc-toolbar button:hover {
                background-color: #334155;
              }
              .attendance-calendar .rbc-toolbar button.rbc-active {
                background-color: #0d9488;
                color: white;
                border-color: #0d9488;
              }
              .attendance-calendar .rbc-month-view {
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                overflow: hidden;
              }
              .dark .attendance-calendar .rbc-month-view {
                border-color: #475569;
              }
              .attendance-calendar .rbc-day-bg + .rbc-day-bg,
              .attendance-calendar .rbc-month-row + .rbc-month-row {
                border-color: #e5e7eb;
              }
              .dark .attendance-calendar .rbc-day-bg + .rbc-day-bg,
              .dark .attendance-calendar .rbc-month-row + .rbc-month-row {
                border-color: #475569;
              }
              @media print {
                .attendance-calendar .rbc-toolbar { display: none; }
              }
            `}</style>
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              defaultView={Views.MONTH}
              views={['month']}
              date={calendarDate}
              onNavigate={handleNavigate}
              onSelectEvent={handleSelectEvent}
              selectable
              onSelectSlot={handleSelectSlot}
              components={{
                event: EventComponent
              }}
              dayPropGetter={dayPropGetter}
              popup
              messages={{
                today: 'Today',
                previous: 'Previous',
                next: 'Next',
              }}
            />
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-6 mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Present (Office)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Remote</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 dark:bg-slate-600 rounded"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">No Data / Weekend</span>
            </div>
          </div>
        </div>
      )}

      {/* Team Reports View */}
      {attendanceViewMode === 'team' && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-teal-600" />
              Team Performance - {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="attendance-team-grid">
            {employees.map(emp => {
              const empRecords = attendanceData?.records?.filter(r => r.user_id === (emp.user_id || emp.id)) || [];
              const presentDays = empRecords.filter(r => (r.status || r.location_status) === 'present').length;
              const remoteDays = empRecords.filter(r => (r.status || r.location_status) === 'remote').length;
              const totalHours = empRecords.reduce((sum, r) => sum + (r.total_hours || 0), 0);
              
              // Calculate working days for the month (excluding Sundays)
              const year = calendarDate.getFullYear();
              const month = calendarDate.getMonth();
              const startDate = new Date(year, month, 1);
              const endDate = new Date() < new Date(year, month + 1, 0) ? new Date() : new Date(year, month + 1, 0);
              let workingDays = 0;
              for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                if (d.getDay() !== 0) workingDays++;
              }
              
              const attendanceRate = workingDays > 0 ? Math.round((empRecords.length / workingDays) * 100) : 0;
              
              return (
                <div 
                  key={emp.id} 
                  className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer border border-transparent hover:border-teal-300 dark:hover:border-teal-700"
                  onClick={() => viewEmployeeReport(emp)}
                  data-testid={`team-card-${emp.id}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-md">
                      <span className="text-white font-bold text-lg">{emp.full_name?.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 dark:text-white truncate">{emp.full_name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{emp.position || emp.department || emp.email}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-2 text-center">
                      <p className="text-xl font-bold text-teal-600">{attendanceRate}%</p>
                      <p className="text-xs text-gray-500">Attendance</p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-2 text-center">
                      <p className="text-xl font-bold text-purple-600">{totalHours.toFixed(0)}h</p>
                      <p className="text-xs text-gray-500">Total Hours</p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-2 text-center">
                      <p className="text-xl font-bold text-green-600">{presentDays}</p>
                      <p className="text-xs text-gray-500">Office Days</p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-2 text-center">
                      <p className="text-xl font-bold text-blue-600">{remoteDays}</p>
                      <p className="text-xs text-gray-500">Remote Days</p>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progress</span>
                      <span>{empRecords.length}/{workingDays} days</span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-teal-500 to-green-500 transition-all duration-500"
                        style={{ width: `${Math.min(attendanceRate, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day Details Dialog */}
      <Dialog open={!!selectedDateRecords} onOpenChange={() => setSelectedDateRecords(null)}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">
              Attendance - {selectedDateRecords?.date && moment(selectedDateRecords.date).format('dddd, MMMM D, YYYY')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {selectedDateRecords?.records?.map((record, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                    <span className="text-teal-600 font-semibold text-sm">{record.user_name?.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 dark:text-white text-sm">{record.user_name}</p>
                    <p className="text-xs text-gray-500">
                      {record.check_in_time_pkt || '-'} - {record.check_out_time_pkt || 'ongoing'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className={`text-xs ${
                    (record.status || record.location_status) === 'present' 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  }`}>
                    {record.status || record.location_status}
                  </Badge>
                  {record.total_hours && (
                    <p className="text-xs text-gray-500 mt-1">{record.total_hours.toFixed(1)}h</p>
                  )}
                </div>
              </div>
            ))}
            {(!selectedDateRecords?.records || selectedDateRecords.records.length === 0) && (
              <p className="text-center text-gray-500 py-4">No attendance records for this day</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Individual Employee Report Dialog */}
      <Dialog open={!!selectedEmployee} onOpenChange={() => { setSelectedEmployee(null); setEmployeeReport(null); }}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              {selectedEmployee?.user_name || selectedEmployee?.full_name} - Monthly Report
            </DialogTitle>
          </DialogHeader>
          
          {loadingReport ? (
            <div className="py-12 text-center">
              <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-gray-500">Loading report...</p>
            </div>
          ) : employeeReport && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-teal-600">{employeeReport.stats.attendanceRate}%</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Attendance Rate</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{employeeReport.stats.presentDays}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Office Days</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{employeeReport.stats.remoteDays}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Remote Days</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-purple-600">{employeeReport.stats.totalHours}h</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Total Hours</p>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 text-center">
                  <p className="text-lg font-semibold text-gray-800 dark:text-white">{employeeReport.stats.workingDays}</p>
                  <p className="text-xs text-gray-500">Working Days</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 text-center">
                  <p className="text-lg font-semibold text-green-600">{employeeReport.stats.onTimeDays}</p>
                  <p className="text-xs text-gray-500">On Time</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 text-center">
                  <p className="text-lg font-semibold text-orange-600">{employeeReport.stats.lateDays}</p>
                  <p className="text-xs text-gray-500">Late Arrivals</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 text-center">
                  <p className="text-lg font-semibold text-gray-800 dark:text-white">{employeeReport.stats.avgHours}h</p>
                  <p className="text-xs text-gray-500">Avg Hours/Day</p>
                </div>
              </div>

              {/* Attendance Log */}
              <div>
                <h4 className="font-semibold text-gray-800 dark:text-white mb-3">Attendance Log</h4>
                <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-slate-700 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-300">Date</th>
                        <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-300">Status</th>
                        <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-300">In</th>
                        <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-300">Out</th>
                        <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-300">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeReport.records.map((record, idx) => (
                        <tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
                          <td className="py-2 px-3 text-gray-800 dark:text-white">
                            {new Date(record.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', weekday: 'short' })}
                          </td>
                          <td className="py-2 px-3">
                            <Badge className={`text-xs ${
                              (record.status || record.location_status) === 'present' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {record.status || record.location_status}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                            {record.check_in_time_pkt || formatTime(record.check_in_time)}
                          </td>
                          <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                            {record.check_out_time_pkt || formatTime(record.check_out_time)}
                          </td>
                          <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                            {record.total_hours?.toFixed(1) || '-'}h
                          </td>
                        </tr>
                      ))}
                      {employeeReport.records.length === 0 && (
                        <tr>
                          <td colSpan="5" className="py-8 text-center text-gray-500">
                            No attendance records for this month
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceTab;
